import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({ baseURL: API_URL, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use(
  (config) => { const t = localStorage.getItem('token'); if (t) config.headers.Authorization = `Bearer ${t}`; return config; },
  (error)  => Promise.reject(error)
);
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect on the login endpoint itself — let AuthContext show the error toast
      const url = error.config?.url || '';
      if (!url.includes('/auth/login')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth ────────────────────────────────────────────────
export const authAPI = {
  login:                (d) => api.post('/auth/login', d),
  register:             (d) => api.post('/auth/register', d),
  getMe:                ()  => api.get('/auth/me'),
  updateProfile:        (d) => api.put('/auth/profile', d),
  changePassword:       (d) => api.put('/auth/change-password', d),
  getNotificationPrefs: ()  => api.get('/auth/notification-prefs'),
  saveNotificationPrefs:(d) => api.put('/auth/notification-prefs', d),
  updateAvatar:         (d) => api.put('/auth/avatar', d),
};

// ── Projects ────────────────────────────────────────────
export const projectAPI = {
  getAll:           (p)    => api.get('/projects', { params: p }),
  getById:          (id)   => api.get(`/projects/${id}`),
  create:           (d)    => api.post('/projects', d),
  update:           (id,d) => api.put(`/projects/${id}`, d),
  delete:           (id)   => api.delete(`/projects/${id}`),
  addTeamMember:    (id,d) => api.post(`/projects/${id}/team`, d),
  removeTeamMember: (id,u) => api.delete(`/projects/${id}/team/${u}`),
  getActivity:      (id)   => api.get(`/projects/${id}/activity`),
  getInvoice:        (id)   => api.get(`/projects/${id}/invoice`),
  // templates
  getTemplates:     ()     => api.get('/projects/templates/list'),
  saveAsTemplate:   (id,d) => api.post(`/projects/${id}/save-template`, d),
  createFromTemplate:(tid,d)=> api.post(`/projects/from-template/${tid}`, d),
  // deadline alerts
  getDeadlineAlerts:(days) => api.get('/projects/alerts/deadlines', { params: { days } }),
  // daily updates
  getUpdates:       (id, params) => api.get(`/projects/${id}/updates`, { params }),
  addUpdate:        (id, d)      => api.post(`/projects/${id}/updates`, d),
  deleteUpdate:     (id, uid)    => api.delete(`/projects/${id}/updates/${uid}`),
};

// ── Tasks ───────────────────────────────────────────────
export const taskAPI = {
  getAll:          (p)        => api.get('/tasks', { params: p }),
  getById:         (id)       => api.get(`/tasks/${id}`),
  create:          (d)        => api.post('/tasks', d),
  update:          (id,d)     => api.put(`/tasks/${id}`, d),
  bulkUpdate:      (d)        => api.post('/tasks/bulk-update', d),
  delete:          (id)       => api.delete(`/tasks/${id}`),
  addComment:      (id,d)     => api.post(`/tasks/${id}/comments`, d),
  updateChecklist: (id,iid,d) => api.put(`/tasks/${id}/checklist/${iid}`, d),
  deleteChecklist: (id,iid)   => api.delete(`/tasks/${id}/checklist/${iid}`),
  addChecklist:    (id,d)     => api.post(`/tasks/${id}/checklist`, d),
  getActivity:     (id)       => api.get(`/tasks/${id}/activity`),
};

// ── Task Templates ──────────────────────────────────────
export const taskTemplateAPI = {
  getAll:  ()          => api.get('/task-templates'),
  create:  (d)         => api.post('/task-templates', d),
  update:  (id,d)      => api.put(`/task-templates/${id}`, d),
  remove:  (id)        => api.delete(`/task-templates/${id}`),
  apply:   (id,projId,data) => api.post(`/task-templates/${id}/apply/${projId}`, data||{}),
};

// ── Users ───────────────────────────────────────────────
export const userAPI = {
  getAll:   (p)    => api.get('/users', { params: p }),
  getById:  (id)   => api.get(`/users/${id}`),
  create:   (d)    => api.post('/users', d),
  update:   (id,d) => api.put(`/users/${id}`, d),
  delete:   (id)   => api.delete(`/users/${id}`),
  getWorkload: (p) => api.get('/users/workload', { params: p }),
  getCelebrations: () => api.get('/users/upcoming/celebrations'),
  offboard:   (id, d) => api.post(`/users/${id}/offboard`, d),
  reactivate: (id)    => api.post(`/users/${id}/reactivate`),
};

// ── Time Logs ───────────────────────────────────────────
export const timelogAPI = {
  getAll:       (p)    => api.get('/timelogs', { params: p }),
  create:       (d)    => api.post('/timelogs', d),
  delete:       (id)   => api.delete(`/timelogs/${id}`),
  getSummary:   ()     => api.get('/timelogs/summary'),
  getPending:   ()     => api.get('/timelogs/pending/list'),
  approve:      (id,d) => api.patch(`/timelogs/${id}/approve`, d),
  bulkApprove:  (d)    => api.post('/timelogs/bulk-approve', d),
};

// ── Attendance ──────────────────────────────────────────
export const attendanceAPI = {
  getToday:    ()  => api.get('/attendance/today'),
  checkIn:     (d) => api.post('/attendance/checkin', d),
  checkOut:    ()  => api.post('/attendance/checkout'),
  startBreak:  (d) => api.post('/attendance/break/start', d),
  endBreak:    ()  => api.post('/attendance/break/end'),
  getReport:   (p) => api.get('/attendance/report', { params: p }),
  getAllToday:  ()  => api.get('/attendance/all-today'),
  manualMark:  (d) => api.post('/attendance/manual', d),
  getSummary:  (p) => api.get('/attendance/summary', { params: p }),
};

// ── Leaves ──────────────────────────────────────────────
export const leaveAPI = {
  apply:         (d)    => api.post('/leaves', d),
  getAll:        (p)    => api.get('/leaves', { params: p }),
  getMyLeaves:   (p)    => api.get('/leaves/my', { params: p }),
  cancelLeave:   (id)   => api.patch(`/leaves/${id}/cancel`),
  getPending:    ()     => api.get('/leaves/pending'),
  processLeave:  (id,d) => api.patch(`/leaves/${id}/process`, d),
  updateStatus:  (id,d) => api.put(`/leaves/${id}/status`, d),
  getBalance:    (p)    => api.get('/leaves/balance', { params: p }),
  updateBalance: (d)    => api.post('/leaves/balance/update', d),
  getTeamSummary:(p)    => api.get('/leaves/team-summary', { params: p }),
};

// ── Salary ──────────────────────────────────────────────
export const salaryAPI = {
  getAll:       (p)    => api.get('/salary/all', { params: p }),
  getMine:      ()     => api.get('/salary/my'),
  getById:      (id)   => api.get(`/salary/payslip/${id}`),
  getStructure: (id)   => api.get(`/salary/structure/${id || 'me'}`),
  setStructure: (d)    => api.post('/salary/structure', d),
  generate:     (d)    => api.post('/salary/generate', d),
  generateBulk: (d)    => api.post('/salary/generate-bulk', d),
  markPaid:     (id,d) => api.patch(`/salary/payslip/${id}/mark-paid`, d),
};

// ── Gmail ───────────────────────────────────────────────
export const gmailAPI = {
  getAuthUrl:      () => api.get('/gmail/auth-url'),
  connect:         (c)=> api.post('/gmail/connect', { code: c }),
  processEmails:   () => api.post('/gmail/process-emails'),
  sendDailyDigest: () => api.post('/gmail/send-daily-digest'),
};

// ── Holidays ────────────────────────────────────────────
export const holidayAPI = {
  getAll:  (p)    => api.get('/holidays', { params: p }),
  create:  (d)    => api.post('/holidays', d),
  update:  (id,d) => api.put(`/holidays/${id}`, d),
  delete:  (id)   => api.delete(`/holidays/${id}`),
};

// ── Notifications ────────────────────────────────────────
export const notificationAPI = {
  getAll:           (p) => api.get('/notifications', { params: p }),
  markRead:         (id)=> api.patch(`/notifications/${id}/read`),
  markAllRead:      ()  => api.patch('/notifications/mark-all-read'),
  deleteOne:        (id)=> api.delete(`/notifications/${id}`),
  deleteAll:        ()  => api.delete('/notifications/clear-all'),
  sendProjectRequest:(d)=> api.post('/notifications/project-request', d),
};

// ── Search ───────────────────────────────────────────────
export const searchAPI = {
  global: (q) => api.get('/users/search/global', { params: { q } }),
};

// ── Dashboard ────────────────────────────────────────────
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard'),
};

export default api;

// ── Profit Analytics ─────────────────────────────────────
export const profitAPI = {
  getMonthly: (months) => api.get('/salary/analytics/profit', { params: { months } }),
};

// ── Google Drive ──────────────────────────────────────────────────────────────
export const driveAPI = {
  getAuthUrl:        ()           => api.get('/drive/auth-url'),
  getStatus:         ()           => api.get('/drive/status'),
  disconnect:        ()           => api.delete('/drive/disconnect'),
  createFolder:      (projectId)  => api.post(`/drive/projects/${projectId}/folder`),
  listFiles:         (projectId, params) => api.get(`/drive/projects/${projectId}/files`, { params }),
  getResumableUrl:   (projectId, data)   => api.post(`/drive/projects/${projectId}/resumable-url`, data),
  uploadFile:        (projectId, data)   => api.post(`/drive/projects/${projectId}/upload`, data),
  createDoc:         (projectId, data)   => api.post(`/drive/projects/${projectId}/create-doc`, data),
  deleteFile:        (fileId)     => api.delete(`/drive/files/${fileId}`),
  shareFile:         (fileId, data)      => api.post(`/drive/files/${fileId}/share`, data),
};

// ── Company Settings ──────────────────────────────────────────────────────────
export const companyAPI = {
  get:    ()     => api.get('/auth/company-settings'),
  update: (data) => api.put('/auth/company-settings', data),
};

// ── Invoices ──────────────────────────────────────────────────────────────────
export const invoiceAPI = {
  list:        (params)       => api.get('/invoices', { params }),
  myInvoices:  ()             => api.get('/invoices/my'),
  create:      (data)         => api.post('/invoices', data),
  getOne:      (id)           => api.get(`/invoices/${id}`),
  update:      (id, data)     => api.put(`/invoices/${id}`, data),
  updateStatus:      (id, data) => api.patch(`/invoices/${id}/status`, data),
  addPartialPayment: (id, data) => api.post(`/invoices/${id}/partial-payment`, data),
  remove:            (id)       => api.delete(`/invoices/${id}`),
  nextNumber:        (platform) => api.get('/invoices/next-number', { params: { platform } }),
};


// ── Razorpay ──────────────────────────────────────────────────────────────────
export const razorpayAPI = {
  config:       ()           => api.get('/razorpay/config'),
  createOrder:  (invoiceId)  => api.post('/razorpay/create-order', { invoiceId }),
  verify:       (data)       => api.post('/razorpay/verify', data),
};

// ── Payment Accounts ──────────────────────────────────────────────────────────
export const paymentAccountAPI = {
  list:             ()           => api.get('/payment-accounts'),
  create:           (data)       => api.post('/payment-accounts', data),
  update:           (id, data)   => api.put(`/payment-accounts/${id}`, data),
  remove:           (id)         => api.delete(`/payment-accounts/${id}`),
  suggestForPlatform: (platform) => api.get('/payment-accounts/suggest', { params: { platform } }),
};

// ── Expenses ──────────────────────────────────────────────────────────────────
export const expenseAPI = {
  getAll:   (p)      => api.get('/expenses', { params: p }),
  getStats: (p)      => api.get('/expenses/stats', { params: p }),
  create:   (d)      => api.post('/expenses', d),
  update:   (id,d)   => api.put(`/expenses/${id}`, d),
  delete:   (id)     => api.delete(`/expenses/${id}`),
  approve:  (id,d)   => api.patch(`/expenses/${id}/approve`, d),
};

// ── Audit Log ─────────────────────────────────────────────────────────────────
export const auditAPI = {
  getLogs:  (p)  => api.get('/audit', { params: p }),
  getStats: ()   => api.get('/audit/stats'),
  clearOld: (d)  => api.delete('/audit/clear', { data: d }),
};

// ── Clients CRM ───────────────────────────────────────────────────────────────
export const clientAPI = {
  getAll:      (p)        => api.get('/clients', { params: p }),
  getStats:    ()         => api.get('/clients/stats'),
  getOne:      (id)       => api.get(`/clients/${id}`),
  updateClient:(id,d)     => api.put(`/clients/${id}`, d),
  getNotes:    (id)       => api.get(`/clients/${id}/notes`),
  createNote:  (id,d)     => api.post(`/clients/${id}/notes`, d),
  updateNote:  (id,nid,d) => api.put(`/clients/${id}/notes/${nid}`, d),
  deleteNote:  (id,nid)   => api.delete(`/clients/${id}/notes/${nid}`),
};

// ── Calendar ─────────────────────────────────────────────
export const calendarAPI = {
  getEvents: (month, year) => api.get('/calendar/events', { params: { month, year } }),
};

// ── Leads / CRM ──────────────────────────────────────────
export const leadsAPI = {
  getAll:   (p)    => api.get('/leads', { params: p }),
  getStats: ()     => api.get('/leads/stats'),
  create:   (d)    => api.post('/leads', d),
  update:   (id,d) => api.put(`/leads/${id}`, d),
  delete:   (id)   => api.delete(`/leads/${id}`),
};

// ── Employee Accounts ─────────────────────────────────────────────────────────
export const employeeAccountsAPI = {
  // Advances
  getAdvances:      (p)      => api.get('/employee-accounts/advances', { params: p }),
  createAdvance:    (d)      => api.post('/employee-accounts/advances', d),
  updateAdvanceStatus: (id,d) => api.put(`/employee-accounts/advances/${id}/status`, d),
  recordDeduction:  (id,d)   => api.put(`/employee-accounts/advances/${id}/deduction`, d),
  deleteAdvance:    (id)     => api.delete(`/employee-accounts/advances/${id}`),
  // Loans
  getLoans:         (p)      => api.get('/employee-accounts/loans', { params: p }),
  createLoan:       (d)      => api.post('/employee-accounts/loans', d),
  updateLoanStatus: (id,d)   => api.put(`/employee-accounts/loans/${id}/status`, d),
  recordPayment:    (id,d)   => api.put(`/employee-accounts/loans/${id}/payment`, d),
  deleteLoan:       (id)     => api.delete(`/employee-accounts/loans/${id}`),
  // Reimbursements
  getReimbursements:      (p)    => api.get('/employee-accounts/reimbursements', { params: p }),
  createReimbursement:    (d)    => api.post('/employee-accounts/reimbursements', d),
  updateReimbStatus:      (id,d) => api.put(`/employee-accounts/reimbursements/${id}/status`, d),
  deleteReimbursement:    (id)   => api.delete(`/employee-accounts/reimbursements/${id}`),
  // Ledger & Stats
  getLedger: (empId) => api.get(`/employee-accounts/ledger/${empId}`),
  getStats:  ()      => api.get('/employee-accounts/stats'),
};

// ── Upwork ────────────────────────────────────────────────────────────────────
export const upworkAPI = {
  getAll:           ()       => api.get('/upwork'),
  getStats:         ()       => api.get('/upwork/stats'),
  create:           (d)      => api.post('/upwork', d),
  update:           (id,d)   => api.put(`/upwork/${id}`, d),
  delete:           (id)     => api.delete(`/upwork/${id}`),
  // Milestones
  addMilestone:     (id,d)   => api.post(`/upwork/${id}/milestones`, d),
  updateMilestone:  (id,mid,d) => api.put(`/upwork/${id}/milestones/${mid}`, d),
  deleteMilestone:  (id,mid) => api.delete(`/upwork/${id}/milestones/${mid}`),
  // Cycles
  addCycle:         (id,d)   => api.post(`/upwork/${id}/cycles`, d),
  updateCycle:      (id,cid,d) => api.put(`/upwork/${id}/cycles/${cid}`, d),
  deleteCycle:      (id,cid) => api.delete(`/upwork/${id}/cycles/${cid}`),
};
